import { useIntl } from "react-intl"
import { useCallback, useRef, useState } from "react"
import { CustomTable, CustomTableRef, InputDate } from "medere-ui-framework"
import { Column } from "primereact/column"
import { Button } from "primereact/button"
import { Dropdown } from "primereact/dropdown"
import { ConfirmDialog } from "primereact/confirmdialog"
import "./HonorariumLiquidationView.scss"
import { getLiquidations, LiquidationFilters } from "../../servicesHooks/Honorarium/useHonorariumLiquidationService"
import { parseApiError } from "../../services/apiError"
import {
    HonorariumLiquidationDTO,
    HonorariumLiquidationProfessionalDTO,
    HonorariumLiquidationStatusId,
} from "../../dto/HonorariumLiquidationDTO"
import { formatMoney } from "../../utils/money"
import { formatDateOnly, toDateOnly } from "../../utils/date"
import useNotify from "../../hooks/useNotify"
import EditLiquidationDialog from "./EditLiquidationDialog"
import HonorariumLiquidationDetailView from "./HonorariumLiquidationDetailView"
import HonorariumLiquidationProfessionalView from "./HonorariumLiquidationProfessionalView"
import LiquidationPaymentStatusTag from "./LiquidationPaymentStatusTag"
import LiquidationStatusTag from "./LiquidationStatusTag"
import NewLiquidationPanel from "./NewLiquidationPanel"

type ViewMode = 'list' | 'create' | 'detail' | 'professional';

const STATUS_OPTIONS = [
    { label: 'Abierta', value: 'OPEN' },
    { label: 'Cerrada', value: 'CLOSED' },
];

/** Rango por defecto del listado: los últimos 3 meses. */
function defaultFromDate(): Date {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date;
}

function toFilters(fromDate: Date | null, toDate: Date | null, status: string | null): LiquidationFilters {
    return {
        fromDate: fromDate ? toDateOnly(fromDate) : undefined,
        toDate: toDate ? toDateOnly(toDate) : undefined,
        status: status ?? undefined,
    };
}

export default function HonorariumLiquidationView() {
    const intl = useIntl();
    const notify = useNotify();
    const tableRef = useRef<CustomTableRef>(null);

    const [mode, setMode] = useState<ViewMode>('list');
    const [liquidationId, setLiquidationId] = useState<number | null>(null);
    const [professional, setProfessional] = useState<HonorariumLiquidationProfessionalDTO | null>(null);
    const [editing, setEditing] = useState<HonorariumLiquidationDTO | null>(null);

    // ── Filtros: se aplican con "Buscar" y se conservan al volver del detalle ──
    const [fromDate, setFromDate] = useState<Date | null>(defaultFromDate);
    const [toDate, setToDate] = useState<Date | null>(() => new Date());
    const [status, setStatus] = useState<string | null>(null);
    const appliedFiltersRef = useRef<LiquidationFilters>(toFilters(defaultFromDate(), new Date(), null));

    const getLiquidationsFiltered = useCallback(async (offset: number, pageSize: number) => {
        try {
            return await getLiquidations(offset, pageSize, appliedFiltersRef.current);
        } catch (error: unknown) {
            parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
            notify.error('No se pudieron cargar las liquidaciones. Intentá de nuevo.');
            // CustomTable no captura el error: sin esto queda el spinner colgado
            return [];
        }
    }, [notify]);

    function handleSearch() {
        appliedFiltersRef.current = toFilters(fromDate, toDate, status);
        tableRef.current?.refreshTable?.();
    }

    function handleClearFilters() {
        const from = defaultFromDate();
        const to = new Date();
        setFromDate(from);
        setToDate(to);
        setStatus(null);
        appliedFiltersRef.current = toFilters(from, to, null);
        tableRef.current?.refreshTable?.();
    }

    const openDetail = useCallback((id: number) => {
        setLiquidationId(id);
        setProfessional(null);
        setMode('detail');
    }, []);

    // Al volver, la tabla se monta de nuevo y trae los datos actualizados
    const backToList = useCallback(() => {
        setLiquidationId(null);
        setMode('list');
    }, []);

    const backToDetail = useCallback(() => {
        setProfessional(null);
        setMode('detail');
    }, []);

    function renderRowActions(row: HonorariumLiquidationDTO) {
        const isOpen = row.statusId === HonorariumLiquidationStatusId.OPEN;
        return (
            <div className="flex justify-content-center">
                <Button
                    text
                    rounded
                    icon="pi pi-eye"
                    className="md-icon-btn"
                    aria-label={`Ver detalle de la liquidación N° ${row.liquidationNumber}`}
                    tooltip="Ver detalle"
                    tooltipOptions={{ position: 'left' }}
                    onClick={() => openDetail(row.honorariumLiquidation)}
                />
                <Button
                    text
                    rounded
                    icon="pi pi-pencil"
                    className="md-icon-btn"
                    aria-label={`Editar la liquidación N° ${row.liquidationNumber}`}
                    tooltip={isOpen ? 'Editar' : 'Ya está cerrada: no se puede editar'}
                    tooltipOptions={{ position: 'left', showOnDisabled: true }}
                    disabled={!isOpen}
                    onClick={() => setEditing(row)}
                />
            </div>
        );
    }

    if (mode === 'create') {
        return (
            <div className="honorarium-liquidation px-4 flex flex-column viewContainer">
                <NewLiquidationPanel onBack={backToList} onCreated={openDetail} />
            </div>
        );
    }

    if (mode === 'detail' && liquidationId !== null) {
        return (
            <div className="honorarium-liquidation px-4 flex flex-column viewContainer">
                <ConfirmDialog />
                <HonorariumLiquidationDetailView
                    liquidationId={liquidationId}
                    onBack={backToList}
                    onSelectProfessional={(selected) => { setProfessional(selected); setMode('professional'); }}
                />
            </div>
        );
    }

    if (mode === 'professional' && liquidationId !== null && professional) {
        return (
            <div className="honorarium-liquidation px-4 flex flex-column viewContainer">
                <ConfirmDialog />
                <HonorariumLiquidationProfessionalView
                    liquidationId={liquidationId}
                    liquidationProfessionalId={professional.honorariumLiquidationProfessional}
                    onBack={backToDetail}
                />
            </div>
        );
    }

    return (
        <div className="honorarium-liquidation px-4 flex flex-column viewContainer">
            <div className="md-header-row">
                <h1 className="md-page-title">Liquidación de honorarios</h1>
                <Button label="Crear" icon="pi pi-plus-circle" className="md-primary" onClick={() => setMode('create')} />
            </div>

            <div className="md-filters">
                <InputDate
                    label="Desde"
                    value={fromDate}
                    onChange={(e: { value: Date | null }) => setFromDate(e.value)}
                    style={{ width: '150px' }}
                />
                <InputDate
                    label="Hasta"
                    value={toDate}
                    onChange={(e: { value: Date | null }) => setToDate(e.value)}
                    style={{ width: '150px' }}
                />
                {/* Mismo contenedor y label que Combobox, para que la barra quede alineada */}
                <div className="inputContainer flexible--column" style={{ width: '180px' }}>
                    <label htmlFor="liquidation-status-filter" className="label inputLabel">Estado</label>
                    <Dropdown
                        inputId="liquidation-status-filter"
                        value={status}
                        options={STATUS_OPTIONS}
                        onChange={(e) => setStatus(e.value ?? null)}
                        placeholder="Todos"
                        showClear
                    />
                </div>
                <Button
                    outlined
                    className="md-ghost md-filter-action"
                    icon="pi pi-search"
                    label={intl.formatMessage({ id: 'Search', defaultMessage: 'Buscar' })}
                    onClick={handleSearch}
                />
                <Button
                    text
                    rounded
                    icon="pi pi-eraser"
                    className="md-icon-btn md-filter-action"
                    aria-label="Limpiar filtros"
                    tooltip="Limpiar filtros"
                    tooltipOptions={{ position: 'top' }}
                    onClick={handleClearFilters}
                />
            </div>

            <CustomTable
                idTable="tableHonorariumLiquidations"
                ref={tableRef}
                getItems={getLiquidationsFiltered}
                className="crudTable"
                filterRow={false}
                hideFiltersButton
                notFoundText="No hay resultados para estos filtros."
            >
                <Column field="liquidationNumber" header="N°" className="text-center" headerClassName="col-center" style={{ width: '80px' }} />
                <Column field="description" header="Descripción" body={(row: HonorariumLiquidationDTO) => row.description || '—'} />
                <Column
                    header="Período"
                    body={(row: HonorariumLiquidationDTO) => `${formatDateOnly(row.fromDate)} – ${formatDateOnly(row.toDate)}`}
                    style={{ width: '200px' }}
                />
                <Column field="billingEntityName" header="Entidad facturadora" />
                <Column field="professionalCount" header="Profesionales" className="text-center" headerClassName="col-center" style={{ width: '110px' }} />
                <Column
                    header="Estado"
                    body={(row: HonorariumLiquidationDTO) => <LiquidationStatusTag statusId={row.statusId} status={row.status} />}
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '110px' }}
                />
                <Column
                    header="Pago"
                    body={(row: HonorariumLiquidationDTO) => <LiquidationPaymentStatusTag paymentStatus={row.paymentStatus} />}
                    className="text-center"
                    headerClassName="col-center"
                    style={{ width: '150px' }}
                />
                <Column
                    header="Honorarios"
                    body={(row: HonorariumLiquidationDTO) => formatMoney(row.totalHonorarium)}
                    className="text-right"
                    headerClassName="col-right"
                    style={{ width: '150px' }}
                />
                <Column
                    header="Neto a pagar"
                    body={(row: HonorariumLiquidationDTO) => formatMoney(row.netToPay)}
                    className="text-right honorarium-liquidation__amount"
                    headerClassName="col-right"
                    style={{ width: '150px' }}
                />
                <Column header="" body={renderRowActions} style={{ width: '96px' }} />
            </CustomTable>

            <EditLiquidationDialog
                liquidation={editing}
                onHide={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    tableRef.current?.refreshTable?.();
                }}
            />
        </div>
    );
}
